<template>
  <section class="form-list profile-page">
    <div class="profile-layout">
      <el-card class="profile-left" shadow="never">
        <div class="profile-avatar">
          <v-avatar :value="profile.avatar" :size="80"/>
        </div>
        <div class="profile-name">{{ profile.nickname || '未设置昵称' }}</div>
        <div class="profile-username">用户名：{{ profile.username }}</div>

        <div class="profile-meta">
          <div class="profile-meta-item">
            <span class="profile-meta-label">所属门店</span>
            <div class="profile-meta-value">{{ storeText || '未绑定门店' }}</div>
          </div>
          <div class="profile-meta-item">
            <span class="profile-meta-label">最后登录</span>
            <div class="profile-meta-value">{{ formatTime(profile.last_login_time) }}</div>
          </div>
          <div class="profile-meta-item">
            <span class="profile-meta-label">创建时间</span>
            <div class="profile-meta-value">{{ formatTime(profile.add_time) }}</div>
          </div>
          <div class="profile-meta-item">
            <span class="profile-meta-label">当前手机号</span>
            <div class="profile-meta-value">{{ profile.oldPhone || '未绑定' }}</div>
          </div>
        </div>

        <el-button type="primary" plain class="profile-offboard-btn" @click="openOffboardDialog">发起离职申请</el-button>
      </el-card>

      <el-card class="profile-right" shadow="never">
        <el-alert
          v-if="isAdministrator"
          type="info"
          :closable="false"
          show-icon
          class="profile-admin-tip"
        >
          超管修改资料请使用【管理员管理】；本页为本人自助修改，手机号变更需短信验证。
        </el-alert>
        <el-tabs v-model="activeTab" @tab-click="handleTabClick">
          <el-tab-pane label="个人资料" name="profile">
            <el-form
              ref="form"
              :model="profile"
              :rules="rules"
              :validate-on-rule-change="false"
              label-position="right"
              label-width="110px"
              class="profile-form"
            >
              <el-form-item label="用户名">
                <el-input :value="profile.username" disabled/>
                <div class="form-input-tip form-input">用户名不可修改</div>
              </el-form-item>

              <el-form-item label="昵称" prop="nickname">
                <el-input v-model="profile.nickname" maxlength="14" show-word-limit clearable/>
              </el-form-item>

              <v-image-upload
                v-model="profile.avatar"
                label="头像"
                prefix="administrator"
                ratio="1:1"
                prop-name="avatar"
                :is-edit="true"
                :is-show-close="false"
              />

              <el-form-item label="更换手机号">
                <div class="phone-input-row">
                  <el-input v-model="profile.phone" maxlength="11" clearable placeholder="输入新手机号"/>
                  <el-button :disabled="!canSendPhoneCode || phoneCodeCountdown > 0" :loading="isSendingSms" @click="sendSms">{{ phoneCodeCountdown > 0 ? phoneCodeCountdown + ' 秒后重发' : '发送验证码' }}</el-button>
                </div>
                <div class="phone-tip">
                  <template v-if="phoneCodeCountdown > 0">
                    验证码已发送至 {{ codeSentPhone }}，{{ phoneCodeCountdown }} 秒后可重新发送
                  </template>
                  <template v-else-if="isPhoneChanged">
                    请输入新手机号后点击发送验证码，验证通过后再保存
                  </template>
                  <template v-else>手机号未修改，无需短信验证</template>
                </div>
              </el-form-item>

              <el-form-item v-if="isPhoneChanged && profile.phone" label="短信验证码" prop="code">
                <el-input v-model="profile.code" maxlength="6" clearable placeholder="填写收到的 6 位验证码"/>
              </el-form-item>

              <el-form-item label="企业微信号">
                <el-input v-model="profile.we_chat" maxlength="32" show-word-limit clearable placeholder="入职审核通过后已按手机号自动绑定"/>
              </el-form-item>

              <el-form-item>
                <div class="profile-save-row">
                  <el-button type="primary" :disabled="!isProfileDirty" :loading="isSaving" @click="handleSubmit">保存资料</el-button>
                  <span v-if="isProfileDirty" class="unsaved-tip">有未保存的修改</span>
                  <span v-else class="saved-tip">当前没有需要保存的修改</span>
                </div>
              </el-form-item>
            </el-form>
          </el-tab-pane>

          <el-tab-pane label="修改密码" name="password">
            <el-radio-group v-model="passwordMode" class="password-mode" @change="handlePasswordModeChange">
              <el-radio-button label="old">原密码修改</el-radio-button>
              <el-radio-button label="sms">短信验证码修改</el-radio-button>
            </el-radio-group>

            <el-form
              v-if="passwordMode === 'old'"
              ref="passwordForm"
              :model="passwordForm"
              :rules="passwordRules"
              :validate-on-rule-change="false"
              label-position="right"
              label-width="110px"
              class="profile-form"
            >
              <el-form-item label="原密码" prop="old_password">
                <el-input v-model="passwordForm.old_password" type="password" show-password maxlength="16"/>
              </el-form-item>
              <el-form-item label="新密码" prop="user_password">
                <el-input v-model="passwordForm.user_password" type="password" show-password maxlength="16"/>
              </el-form-item>
              <el-form-item>
                <el-button type="primary" :loading="isSavingPassword" @click="changePassword">修改密码</el-button>
              </el-form-item>
            </el-form>

            <el-form
              v-else
              ref="smsPasswordForm"
              :model="smsPasswordForm"
              :rules="smsPasswordRules"
              :validate-on-rule-change="false"
              label-position="right"
              label-width="110px"
              class="profile-form"
            >
              <el-form-item label="当前手机号">
                <el-input :value="profile.oldPhone || '未绑定手机号'" disabled/>
              </el-form-item>
              <el-form-item label="新密码" prop="user_password">
                <el-input v-model="smsPasswordForm.user_password" type="password" show-password maxlength="16"/>
              </el-form-item>
              <el-form-item label="短信验证码" prop="code">
                <div class="phone-input-row">
                  <el-input v-model="smsPasswordForm.code" maxlength="6" clearable placeholder="6 位短信验证码"/>
                  <el-button :disabled="!canSendPasswordSms || passwordCodeCountdown > 0" :loading="isSendingPasswordSms" @click="sendPasswordSms">{{ passwordCodeCountdown > 0 ? passwordCodeCountdown + ' 秒后重发' : '发送验证码' }}</el-button>
                </div>
                <div class="phone-tip">
                  <template v-if="passwordCodeCountdown > 0">
                    验证码已发送，{{ passwordCodeCountdown }} 秒后可重新发送
                  </template>
                  <template v-else-if="canSendPasswordSms">
                    验证码发送至当前手机号
                  </template>
                  <template v-else>当前账号未绑定手机号，请联系管理员处理</template>
                </div>
              </el-form-item>
              <el-form-item>
                <el-button type="primary" :loading="isSavingPasswordBySms" @click="changePasswordBySms">重置密码</el-button>
              </el-form-item>
            </el-form>
          </el-tab-pane>
        </el-tabs>
      </el-card>
    </div>

    <el-dialog title="发起离职申请" :visible.sync="isOffboardDialogVisible" width="460px">
      <el-form ref="offboardForm" :model="offboardForm" :rules="offboardRules" label-width="100px">
        <el-form-item label="离职类型" prop="leave_type">
          <el-select v-model="offboardForm.leave_type" class="w100">
            <el-option v-for="item in leaveTypeOptions" :key="item.value" :label="item.name" :value="item.value"/>
          </el-select>
        </el-form-item>
        <el-form-item label="离职原因" prop="leave_reason">
          <el-input v-model="offboardForm.leave_reason" type="textarea" :rows="3" maxlength="255" show-word-limit placeholder="请填写离职原因"/>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="isOffboardDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="isSavingOffboard" @click="submitOffboard">提交</el-button>
      </div>
    </el-dialog>

  </section>
</template>

<script>
import { getSelfProfile, updateSelfProfile, updatePasswordWithLog, updatePasswordBySms } from '@/api/administrator';
import { sendProfilePhoneSms, sendProfilePasswordSms, offboardApplyStore } from '@/api/personnel';
import { leaveTypes } from '@/enums/leaveTypes';
import { formatStoreName } from '@/filters';

export default {
  name: 'AdministratorProfile',
  data: () => ({
    activeTab: 'profile',
    passwordMode: 'old',
    isSaving: false,
    isSavingPassword: false,
    isSavingPasswordBySms: false,
    isSendingSms: false,
    isSendingPasswordSms: false,
    phoneCodeCountdown: 0,
    codeSentPhone: '',
    passwordCodeCountdown: 0,
    phoneCodeTimer: null,
    passwordCodeTimer: null,
    isOffboardDialogVisible: false,
    isSavingOffboard: false,
    offboardForm: {
      leave_type: 1,
      leave_reason: '',
    },
    offboardRules: {
      leave_reason: [{ required: true, message: '请填写离职原因', trigger: 'blur' }],
    },
    leaveTypeOptions: Object.values(leaveTypes),

    profile: {
      id: 0,
      username: '',
      phone: '',
      oldPhone: '',
      nickname: '',
      we_chat: '',
      avatar: '',
      code: '',
      last_login_time: 0,
      add_time: 0,
      stores: [],
    },
    profileSnapshot: {
      nickname: '',
      phone: '',
      we_chat: '',
      avatar: '',
    },
    passwordForm: {
      old_password: '',
      user_password: '',
    },
    smsPasswordForm: {
      code: '',
      user_password: '',
    },
    rules: {
      nickname: [{ required: true, message: '请输入昵称', trigger: 'change' }],
      code: [{ required: true, message: '请输入短信验证码', trigger: 'change' }],
    },
    passwordRules: {
      old_password: [{ required: true, message: '请输入原密码', trigger: 'blur' }],
      user_password: [{ required: true, message: '请输入新密码', trigger: 'blur' }],
    },
    smsPasswordRules: {
      code: [{ required: true, message: '请输入短信验证码', trigger: 'blur' }],
      user_password: [{ required: true, message: '请输入新密码', trigger: 'blur' }],
    },
  }),
  computed: {
    isPhoneChanged() {
      return this.profile.phone !== this.profile.oldPhone;
    },
    isProfileDirty() {
      const { nickname, phone, we_chat, avatar } = this.profile;
      const snapshot = this.profileSnapshot;
      return (nickname || '') !== (snapshot.nickname || '')
        || (phone || '') !== (snapshot.phone || '')
        || (we_chat || '') !== (snapshot.we_chat || '')
        || (avatar || '') !== (snapshot.avatar || '');
    },
    isPasswordDirty() {
      return !!this.passwordForm.old_password
        || !!this.passwordForm.user_password
        || !!this.smsPasswordForm.code
        || !!this.smsPasswordForm.user_password;
    },
    hasUnsavedChanges() {
      return this.isProfileDirty || this.isPasswordDirty;
    },
    canSendPhoneCode() {
      return this.isPhoneChanged
        && this.profile.phone
        && this.profile.phone.length === 11;
    },
    canSendPasswordSms() {
      return this.profile.oldPhone && this.profile.oldPhone.length === 11;
    },
    storeText() {
      if (!this.profile.stores || !this.profile.stores.length) return '';
      return this.profile.stores
        .map((item) => `${formatStoreName(item.store_name)}（${item.role_name || '未设置职位'}）`)
        .join('、');
    },
    isAdministrator() {
      return this.$store.getters.isAdministrator;
    },
  },
  beforeRouteLeave(to, from, next) {
    if (!this.hasUnsavedChanges) {
      next();
      return;
    }
    this.$confirm('当前有未保存的修改，离开后修改将丢失，确定离开吗？', '未保存提示', {
      type: 'warning',
      confirmButtonText: '离开',
      cancelButtonText: '继续编辑',
    }).then(() => next()).catch(() => next(false));
  },

  async mounted() {
    window.addEventListener('beforeunload', this.handleBeforeUnload);
    await this.loadProfile();
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    this.clearCountdownTimers();
  },
  methods: {
    async loadProfile() {
      const { isError, data } = await getSelfProfile();
      if (isError) return;
      this.profile = {
        ...this.profile,
        ...data,
        phone: data.phone ? String(data.phone) : '',
        oldPhone: data.phone ? String(data.phone) : '',
        code: '',
        stores: data.stores || [],
      };
      this.profileSnapshot = {
        nickname: this.profile.nickname || '',
        phone: this.profile.phone || '',
        we_chat: this.profile.we_chat || '',
        avatar: this.profile.avatar || '',
      };
    },
    handleBeforeUnload(e) {
      if (!this.hasUnsavedChanges) return;
      e.preventDefault();
      e.returnValue = '';
    },
    async sendSms() {
      if (!this.canSendPhoneCode || this.isSendingSms || this.phoneCodeCountdown > 0) return;
      this.isSendingSms = true;
      const { isError } = await sendProfilePhoneSms({ phone: this.profile.phone });
      this.isSendingSms = false;
      if (isError) return;
      this.$message.success('验证码已发送，请查收');
      this.codeSentPhone = this.profile.phone;
      this.startPhoneCodeCountdown();
    },
    async sendPasswordSms() {
      if (!this.canSendPasswordSms || this.isSendingPasswordSms || this.passwordCodeCountdown > 0) return;
      this.isSendingPasswordSms = true;
      const { isError } = await sendProfilePasswordSms({ phone: this.profile.oldPhone });
      this.isSendingPasswordSms = false;
      if (isError) return;
      this.$message.success('验证码已发送，请查收');
      this.startPasswordCodeCountdown();
    },
    changePasswordBySms() {
      if (this.isSavingPasswordBySms) return;
      this.$refs.smsPasswordForm.validate(async (isSuccess) => {
        if (!isSuccess) return;
        if (!this.canSendPasswordSms) {
          this.$message.warning('当前账号未绑定手机号，无法通过短信验证码重置密码');
          return;
        }
        this.isSavingPasswordBySms = true;
        const { isError } = await updatePasswordBySms({
          phone: this.profile.oldPhone,
          code: this.smsPasswordForm.code,
          user_password: this.smsPasswordForm.user_password,
        });
        this.isSavingPasswordBySms = false;
        if (isError) return;
        // 密码修改后必须重新登录：后端已使旧 token 失效，前端主动退出并跳转登录页
        await this.$store.dispatch('user/logout', { is_not_auto_login: 1 });
        this.$message.success('密码已重置，请使用新密码重新登录');
      });
    },
    handleSubmit() {
      if (this.isSaving) return;
      if (!this.isProfileDirty) {
        this.$message.info('资料没有修改，无需保存');
        return;
      }
      if (this.isPhoneChanged && this.profile.phone && !this.profile.code) {
        this.$message.warning('手机号已修改，请先获取并填写短信验证码');
        return;
      }
      this.$refs.form.validate(async (isSuccess) => {
        if (!isSuccess) return;
        this.isSaving = true;
        const { isError } = await updateSelfProfile({
          nickname: this.profile.nickname,
          phone: this.profile.phone || 0,
          code: this.profile.code || 0,
          we_chat: this.profile.we_chat,
          avatar: this.profile.avatar,
        });
        this.isSaving = false;
        if (isError) return;
        this.$message.success('保存成功');
        await this.loadProfile();
      });
    },
    changePassword() {
      if (this.isSavingPassword) return;
      this.$refs.passwordForm.validate(async (isSuccess) => {
        if (!isSuccess) return;
        this.isSavingPassword = true;
        const { isError } = await updatePasswordWithLog(this.passwordForm);
        this.isSavingPassword = false;
        if (isError) return;
        // 密码修改后必须重新登录：后端已使旧 token 失效，前端主动退出并跳转登录页
        await this.$store.dispatch('user/logout', { is_not_auto_login: 1 });
        this.$message.success('密码修改成功，请使用新密码重新登录');
      });
    },
    formatTime(timestamp) {
      if (!timestamp) return '—';
      const value = Number(timestamp);
      if (!value) return '—';
      const date = new Date(value * 1000);
      const pad = (num) => String(num).padStart(2, '0');
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    },
    openOffboardDialog() {
      this.offboardForm.leave_type = 1;
      this.offboardForm.leave_reason = '';
      this.isOffboardDialogVisible = true;
      this.$nextTick(() => {
        if (this.$refs.offboardForm) this.$refs.offboardForm.clearValidate();
      });
    },
    submitOffboard() {
      if (this.isSavingOffboard) return;
      this.$refs.offboardForm.validate(async (isSuccess) => {
        if (!isSuccess) return;
        this.isSavingOffboard = true;
        const { isError, data } = await offboardApplyStore({
          leave_type: this.offboardForm.leave_type,
          leave_reason: this.offboardForm.leave_reason,
        });
        this.isSavingOffboard = false;
        if (isError) return;
        this.isOffboardDialogVisible = false;
        const message = data && data.is_direct
          ? '离职已直接办理'
          : (this.isAdministrator ? '离职申请已提交，请等待另一名超级管理员审核' : '离职申请已提交，请等待门店店长或超级管理员审核');
        this.$message.success(message);
      });
    },
    handleTabClick() {
      this.clearPasswordValidation();
    },
    handlePasswordModeChange() {
      this.clearPasswordValidation();
    },
    clearPasswordValidation() {
      if (this.$refs.passwordForm) this.$refs.passwordForm.clearValidate();
      if (this.$refs.smsPasswordForm) this.$refs.smsPasswordForm.clearValidate();
    },
    startPhoneCodeCountdown() {
      if (this.phoneCodeTimer) clearInterval(this.phoneCodeTimer);
      this.phoneCodeCountdown = 60;
      this.phoneCodeTimer = setInterval(() => {
        this.phoneCodeCountdown -= 1;
        if (this.phoneCodeCountdown <= 0) {
          clearInterval(this.phoneCodeTimer);
          this.phoneCodeTimer = null;
        }
      }, 1000);
    },
    startPasswordCodeCountdown() {
      if (this.passwordCodeTimer) clearInterval(this.passwordCodeTimer);
      this.passwordCodeCountdown = 60;
      this.passwordCodeTimer = setInterval(() => {
        this.passwordCodeCountdown -= 1;
        if (this.passwordCodeCountdown <= 0) {
          clearInterval(this.passwordCodeTimer);
          this.passwordCodeTimer = null;
        }
      }, 1000);
    },
    clearCountdownTimers() {
      if (this.phoneCodeTimer) clearInterval(this.phoneCodeTimer);
      if (this.passwordCodeTimer) clearInterval(this.passwordCodeTimer);
      this.phoneCodeTimer = null;
      this.passwordCodeTimer = null;
    },
  },
};
</script>

<style scoped>
.profile-page {
  padding: 20px;
}

.profile-layout {
  display: flex;
  align-items: flex-start;
  gap: 20px;
}

.profile-left {
  width: 280px;
  flex-shrink: 0;
  text-align: center;
  padding: 30px 20px;
  box-sizing: border-box;
}

.profile-avatar {
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
}

.profile-name {
  font-size: 20px;
  font-weight: bold;
  color: #303133;
  margin-bottom: 6px;
}

.profile-username {
  font-size: 13px;
  color: #909399;
  margin-bottom: 20px;
}

.profile-meta {
  text-align: left;
  border-top: 1px solid #ebeef5;
  padding-top: 16px;
}

.profile-meta-item {
  margin-bottom: 14px;
}

.profile-meta-label {
  display: block;
  font-size: 12px;
  color: #909399;
  margin-bottom: 4px;
}

.profile-meta-value {
  font-size: 14px;
  color: #303133;
  line-height: 1.5;
  word-break: break-all;
}

.profile-right {
  flex: 1;
  min-width: 0;
}

.profile-admin-tip {
  margin-bottom: 16px;
}

.profile-offboard-btn {
  width: 100%;
  margin-top: 8px;
}

.profile-form {
  max-width: 520px;
}

.profile-save-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.unsaved-tip {
  margin-left: 12px;
  font-size: 13px;
  color: #e6a23c;
}

.saved-tip {
  margin-left: 12px;
  font-size: 13px;
  color: #909399;
}

.password-mode {
  margin-bottom: 20px;
}

.phone-input-row {
  display: flex;
  width: 100%;
}

.phone-input-row .el-input {
  flex: 1;
  margin-right: 10px;
}

.phone-tip {
  font-size: 12px;
  color: #909399;
  line-height: 1.6;
  margin-top: 6px;
}
</style>